import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSettings } from '../../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from '../../games/speak';
import { playCorrectPing } from '../../games/ping';
import type { SequenceTapConfig } from '../types';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function gridCols(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

export default function SequenceTap({ config }: { config: SequenceTapConfig }) {
  const { settings } = useSettings();
  const [seqIdx, setSeqIdx] = useState(0);
  const [step, setStep] = useState(0);
  // Track tile uniqueness by index (so repeated item strings still work).
  const [order, setOrder] = useState<number[]>([]);
  const [tappedPositions, setTappedPositions] = useState<number[]>([]);
  const playRef = useRef<() => void>(() => {});

  const seq = config.sequences[seqIdx % Math.max(1, config.sequences.length)];

  useEffect(() => {
    if (!seq) return;
    const indices = seq.items.map((_, i) => i);
    setOrder(shuffle(indices));
    setStep(0);
    setTappedPositions([]);
  }, [seqIdx, seq]);

  const playPrompt = useCallback(() => {
    if (!seq) return;
    if (config.promptMode === 'AUDIO' && canSpeak()) {
      speak(seq.label, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [config.promptMode, seq, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => { playRef.current = playPrompt; }, [playPrompt]);

  useEffect(() => {
    const t = window.setTimeout(() => playRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [seqIdx]);

  useEffect(() => () => stopSpeaking(), []);

  const gridClass = useMemo(() => gridCols(order.length), [order.length]);

  if (!seq) {
    return <div className="card"><p className="muted">No sequences configured.</p></div>;
  }

  function tap(positionInOrder: number) {
    if (tappedPositions.includes(positionInOrder)) return;
    const itemIndex = order[positionInOrder];
    if (itemIndex === step) {
      const nextStep = step + 1;
      const nextTapped = [...tappedPositions, positionInOrder];
      setTappedPositions(nextTapped);
      setStep(nextStep);
      if (nextStep === seq.items.length) {
        if (settings.correctPingEnabled) playCorrectPing();
        window.setTimeout(() => setSeqIdx((i) => i + 1), 900);
      }
    }
  }

  return (
    <>
      <div className="concept-prompt-text" aria-live="polite">{seq.label}</div>
      <div className="concept-sequence-row" aria-label="Sequence progress">
        {seq.items.map((it, i) => (
          <span key={i} className={`concept-seq-slot${i < step ? ' filled' : ''}`}>
            {i < step ? it : i + 1}
          </span>
        ))}
      </div>
      <div className={`tile-grid ${gridClass}`} role="group" aria-label="Sequence tiles">
        {order.map((itemIndex, pos) => {
          const used = tappedPositions.includes(pos);
          return (
            <button
              key={`${pos}-${itemIndex}`}
              className={`tile${used ? ' correct' : ''}`}
              onClick={() => tap(pos)}
              disabled={used}
              aria-label={seq.items[itemIndex]}
            >
              {seq.items[itemIndex]}
            </button>
          );
        })}
      </div>
    </>
  );
}
